/**
 * UPDATES SETTINGS TAB
 *
 * Check for updates, view release notes, download + install.
 * Uses @tauri-apps/plugin-updater in desktop, shows fallback in web.
 */

import { useState, useCallback } from "react";
import { isTauri } from "@/hooks/usePlatform";
import { Button } from "@/components/ui/button";
import { Download, CheckCircle, AlertCircle, Loader2, RefreshCw, ArrowUpCircle } from "lucide-react";

type UpdateState = "idle" | "checking" | "up-to-date" | "available" | "downloading" | "ready" | "error";

interface UpdateInfo {
  version: string;
  date: string;
  body: string;
}

export function UpdatesSettingsTab() {
  const [state, setState] = useState<UpdateState>("idle");
  const [progress, setProgress] = useState<{ downloaded: number; total: number | null }>({ downloaded: 0, total: null });
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentVersion = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0";

  const checkForUpdates = useCallback(async () => {
    if (!isTauri()) return;

    setState("checking");
    setError(null);

    try {
      const { check } = await import("@tauri-apps/plugin-updater");
      const update = await check();

      if (update) {
        setUpdateInfo({
          version: update.version,
          date: update.date ?? "",
          body: update.body ?? "",
        });
        setState("available");

        // Store the update object for later download
        (window as any).__factoryUpdate = update;
      } else {
        setState("up-to-date");
      }
    } catch (e: any) {
      setError(e.message || "Failed to check for updates");
      setState("error");
    }
  }, []);

  const downloadAndInstall = useCallback(async () => {
    const update = (window as any).__factoryUpdate;
    if (!update) return;

    setState("downloading");
    setProgress({ downloaded: 0, total: null });

    try {
      let downloaded = 0;
      let contentLength: number | null = null;

      await update.downloadAndInstall((event: any) => {
        switch (event.event) {
          case "Started":
            contentLength = event.data.contentLength ?? null;
            setProgress({ downloaded: 0, total: contentLength });
            break;
          case "Progress":
            downloaded += event.data.chunkLength;
            setProgress({ downloaded, total: contentLength });
            break;
          case "Finished":
            break;
        }
      });

      setState("ready");
    } catch (e: any) {
      setError(e.message || "Download failed");
      setState("error");
    }
  }, []);

  const relaunch = useCallback(async () => {
    try {
      const { relaunch: relaunchApp } = await import("@tauri-apps/plugin-process");
      await relaunchApp();
    } catch (e: any) {
      setError(e.message || "Failed to relaunch");
      setState("error");
    }
  }, []);

  if (!isTauri()) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        <p>Auto-updates are only available in the desktop app.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-2">
      {/* Version info */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Colony Desktop</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Version {currentVersion}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={checkForUpdates}
          disabled={state === "checking" || state === "downloading"}
        >
          {state === "checking" ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          )}
          {state === "checking" ? "Checking..." : "Check for Updates"}
        </Button>
      </div>

      {/* Status: up to date */}
      {state === "up-to-date" && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 px-4 py-3">
          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-300">You're running the latest version.</p>
        </div>
      )}

      {/* Status: error */}
      {state === "error" && error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-4 py-3">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-700 dark:text-red-300">Update check failed</p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Status: update available */}
      {(state === "available" || state === "downloading" || state === "ready") && updateInfo && (
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowUpCircle className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium">Version {updateInfo.version} available</span>
            </div>
            {updateInfo.date && (
              <span className="text-xs text-muted-foreground">
                {new Date(updateInfo.date).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Download progress */}
          {state === "downloading" && (
            <div className="space-y-1.5">
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{
                    width: progress.total
                      ? `${Math.min(100, (progress.downloaded / progress.total) * 100)}%`
                      : "50%",
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {progress.total
                  ? `${formatBytes(progress.downloaded)} / ${formatBytes(progress.total)}`
                  : `${formatBytes(progress.downloaded)} downloaded...`}
              </p>
            </div>
          )}

          {/* Release notes */}
          {updateInfo.body && (
            <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto whitespace-pre-wrap border-t pt-2">
              {updateInfo.body}
            </div>
          )}

          {/* Action button */}
          {state === "available" && (
            <Button size="sm" onClick={downloadAndInstall}>
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Install Update
            </Button>
          )}
          {state === "downloading" && (
            <Button size="sm" disabled>
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              Downloading...
            </Button>
          )}
          {state === "ready" && (
            <Button size="sm" onClick={relaunch}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Relaunch to Finish
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
